import { Unionize } from "./generic";
import { User } from "./identities";
import { Layer, LayerIds } from "./layer";

export enum ExportStatus {
  Pending = "PENDING",
  InProgress = "IN_PROGRESS",
  Complete = "COMPLETE",
  Error = "ERROR",
}

export const ExportFormats = ["geojson", "shp", "kml", "tab"] as const;
export type ExportFormats = Unionize<typeof ExportFormats>;

const ExportFormatLabels: Record<ExportFormats, string> = {
  geojson: "GeoJSON",
  shp: "ShapeFile",
  kml: "KML",
  tab: "Tab",
};

export const getExportFormat = (format: ExportFormats): string => {
  return ExportFormatLabels[format];
};

export enum SystemsOfMeasurement {
  Metric = "metric",
  Imperial = "imperial",
}

export type ExportStatusDetails = {
  Value: ExportStatus;
  Message?: string;
  Progress?: number;
  JobID?: string;
};

export type ExportLayer = {
  ID: string;
  LayerKey: string;
};

export type ExportLayerWithLabel = {
  Label: string;
} & ExportLayer;

export type LayerWithLabel = {
  Label: string;
} & Layer;

type ConstructionPrintExport = {
  Type: "standard" | "custom";
};

type CreateStandardConstructionPrintExport = {
  Type: "standard";
};

type CreateCustomConstructionPrintExport = {
  Type: "custom";
  Layers: { Layer: { LayerKey: string }; Visibility: { GridMap: boolean; KeyMap: boolean } }[];
};

type CreateConstructionPrintExport = CreateStandardConstructionPrintExport | CreateCustomConstructionPrintExport;

export interface Export {
  ID: string;
  VersionID: string;
  CreatedAt: string;
  Creator: User;
  LastModifiedAt: string;
  LastModifiedBy: User;
  CompletedAt?: string;
  Format: ExportFormats;
  Name?: string;
  Description?: string;
  SystemOfMeasurement: SystemsOfMeasurement;
  BOM?: boolean;
  SpliceTables?: boolean;
  FiberCableSplit?: boolean;
  HubSplit?: boolean;
  SpliceSplit?: boolean;
  Layers: ExportLayer[];
  Status: ExportStatusDetails;
  Url?: string;
  FileSizeBytes?: number;
  InputDataArea: boolean;
  ConstructionPrints?: ConstructionPrintExport | null;
}

export type CreateExportParams = Pick<
  Export,
  "VersionID" | "Description" | "BOM" | "SpliceTables" | "FiberCableSplit" | "HubSplit" | "SpliceSplit"
> & {
  Name: string;
  Format: string;
  SystemOfMeasurement: string;
  Layers: string[];
  ConstructionPrints?: CreateConstructionPrintExport | null;
};

export type ExportsByStatus = {
  InProgress: Export[];
  Complete: Export[];
  Error: Export[];
};

export const SplitByTier = {
  [LayerIds.hub]: "hubSplit",
  [LayerIds.splice]: "spliceSplit",
  [LayerIds.fibreCable]: "fiberCableSplit",
};

export enum ExportAccordionPanel {
  InProgress = "exports-in-progress",
  Complete = "project-exports",
}
